import React, {useEffect, useRef, useState} from "react";
import {IFiltersAndSortProps} from "@/pages/ConstructionSiteDetails/EquipmentsNew/FiltersAndSort/FiltersAndSort";
import {Modal, ModalSize} from "@renta-apps/athenaeum-react-components";
import {useDidUpdateEffect, useResize} from "@/helpers/Hooks";
import {EquipmentsFiltersSortAndPagination} from "@/pages/ConstructionSiteDetails/EquipmentsNew/EquipmentsNew";
import Localizer from "@/localization/Localizer";
import {Button, ButtonSize, ButtonType, IconSize} from "@renta-apps/renta-react-components";
import {getProductsCount} from "@/services/ConstructionSiteService";
import FiltersForm from "./FiltersForm";
import styles from "./FiltersModal.module.scss";
import containerStyles from "./FiltersAndSort.module.scss";

interface IFiltersModalProps extends IFiltersAndSortProps {
    onClose(): void;
}

const FiltersModal: React.FC<IFiltersModalProps> = ({
    onClose,
    params: initialParams,
    constructionSiteId,
    onParamValueChange,
}: IFiltersModalProps) => {
    const _modalRef: React.RefObject<Modal> = useRef(null);
    const [resultsCount, setResultsCount] = useState<number | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(false);
    const [internalFilters, setInternalFilters] = useState<EquipmentsFiltersSortAndPagination>(initialParams);
    const {windowWidth} = useResize();

    useEffect(() => {
        if (!_modalRef.current) {
            return;
        }

        const modalRefLocal = _modalRef.current;
        modalRefLocal?.openAsync();

        return () => {
            modalRefLocal?.closeAsync();
        };
    }, [_modalRef]);

    useDidUpdateEffect(() => {
        getProductsCountAsync().catch();
    }, [internalFilters]);

    const getProductsCountAsync = async () => {
        setIsLoading(true);
        try {
            const {deviceNames, productGroups, statuses, categories, rentalPeriods, renters, contracts, depots} = internalFilters;
            const count = await getProductsCount(
                constructionSiteId,
                deviceNames,
                productGroups,
                statuses,
                categories,
                rentalPeriods,
                renters,
                contracts,
                depots,
            );
            setResultsCount(count);
        } catch (error) {
            console.error("Error fetching products count:", error);
            setResultsCount(undefined);
        } finally {
            setIsLoading(false);
        }
    };

    const handleButtonClick = () => {
        onParamValueChange({
            contracts: internalFilters.contracts,
            depots: internalFilters.depots,
            renters: internalFilters.renters,
            categories: internalFilters.categories,
            deviceNames: internalFilters.deviceNames,
            productGroups: internalFilters.productGroups,
            statuses: internalFilters.statuses,
            rentalPeriods: internalFilters.rentalPeriods,
        });
        onClose();
    };

    return (
        <Modal id="equipment-filters-modal"
               ref={_modalRef}
               preventEsc
               preventClosingOnInsideClick
               preventClosingOnOutsideClick
               size={ModalSize.Default}
               title={Localizer.searchTitle}
               className={styles.modal}
               onClose={async () => onClose()}
               notResponsive={windowWidth >= 576}
        >
            <div className={containerStyles.container}>
                <FiltersForm constructionSiteId={constructionSiteId}
                             params={internalFilters}
                             modalMode
                             idPrefix="filters-modal_"
                             onParamValueChange={(change: Partial<EquipmentsFiltersSortAndPagination>) => setInternalFilters(prev => ({...prev, ...change}))}
                />
            </div>
            <Button className={styles.showResultsButton}
                    type={ButtonType.Primary}
                    size={ButtonSize.Medium}
                    disabled={isLoading}
                    id="filters-modal_search-equipments-button"
                    iconLeft={{name: "magnifyingGlass", color: "white", size: IconSize.Medium}}
                    iconRight={isLoading ? {name: 'fa-spinner fa-spin', color: "white", size: IconSize.Medium} : undefined}
                    onClick={handleButtonClick}
            >
                <div>{Localizer.searchTitle}{resultsCount === undefined ? '' : ` (${resultsCount.toString()})`}</div>
            </Button>
        </Modal>
    );
};

export default FiltersModal;